import axios from 'axios';
import { setRequestDefaults, withRetry } from '../util/util.service';
import { notification } from '../notification/notification.service';
import { config } from '../../configs/app.config';
import TagManager from 'react-gtm-module/dist/TagManager';
import { DateTime } from 'luxon';

/*
v1 API query params:
- page: number (1-indexed)
- limit: number (optional, backend default is 25)
- filter: array of strings "field||$op||value"
- sort: array of strings "field,ASC|DESC"

Note: join is NOT supported - backend auto-includes relations
 */
export const prepareQueryParams = (queryParams) => {
  // Remove unsupported params (join) and pass through supported ones
  const { join, ...rest } = queryParams || {};
  // Note: join is intentionally ignored - backend auto-includes relations
  return rest;
};

/**
 * Build query string for v1 API endpoints
 * Supports: page, limit, filter (array of strings), sort (array of strings)
 * Note: join is not supported - backend auto-includes relations
 *
 * Filter format: field||$operation||value (e.g., "status||$eq||COMPLETED")
 * Sort format: field,ASC|DESC (e.g., "createdAt,DESC")
 */
export const buildV1QueryString = (queryParams) => {
  if (!queryParams) return '';

  const params = new URLSearchParams();

  // Page (1-indexed)
  if (queryParams.page != null) {
    params.append('page', queryParams.page);
  }

  // Limit (optional - backend default is 25)
  if (queryParams.limit != null) {
    params.append('limit', queryParams.limit);
  }

  // Filter: array of strings like "field||$op||value"
  // Skip undefined, null, or empty values
  if (queryParams.filter != null) {
    const filters = Array.isArray(queryParams.filter)
      ? queryParams.filter
      : [queryParams.filter];
    filters.forEach((f) => {
      // Only append valid filter strings
      if (f && typeof f === 'string' && f.includes('||')) {
        params.append('filter', f);
      }
    });
  }

  // Sort: array of strings like "field,ASC" or objects like { field, order }
  if (queryParams.sort != null) {
    const sorts = Array.isArray(queryParams.sort)
      ? queryParams.sort
      : [queryParams.sort];
    sorts.forEach((s) => {
      if (typeof s === 'string' && s) {
        params.append('sort', s);
      } else if (s && s.field) {
        // Convert object format { field, order } to string format "field,ORDER"
        params.append('sort', `${s.field},${s.order || 'ASC'}`);
      }
    });
  }

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
};

/**
 * GET query for v1 API endpoints using simplified query format
 * Supports: page, filter[], sort[] (no limit, no join for manager endpoints)
 */
export const baseGetQueryV1 = (
  path,
  queryParams,
  successMessageId,
  errorMessageId,
  skipRetry,
) => {
  const queryString = buildV1QueryString(queryParams);

  const request = axios.get(`${config.apiBaseUrl}/${path}${queryString}`);
  return (skipRetry ? request : withRetry(() => request))
    .then((res) => {
      console.debug(`GET /${path} result:`, res.data);
      if (successMessageId) notification('success', successMessageId);
      return res.data;
    })
    .catch((err) => {
      console.debug(`GET /${path} error:`, err.message);
      if (errorMessageId) notification('error', errorMessageId);
      throw err;
    });
};

export const baseGetQuery = (
  path,
  queryParams,
  successMessageId,
  errorMessageId,
  skipRetry,
) => {
  // Use v1 query format - remove unsupported params like join
  const cleanParams = prepareQueryParams(queryParams);
  const queryString = buildV1QueryString(cleanParams);

  const request = axios.get(`${config.apiBaseUrl}/${path}${queryString}`);
  return (skipRetry ? request : withRetry(() => request))
    .then((res) => {
      console.debug(`GET /${path} result:`, res.data);
      if (successMessageId) notification('success', successMessageId);
      return res.data;
    })
    .catch((err) => {
      console.debug(`GET /${path} error:`, err.message);
      if (errorMessageId) notification('error', errorMessageId);
      throw err;
    });
};

export const basePostQuery = (
  path,
  data,
  queryParams,
  successMessageId,
  errorMessageId,
  skipRetry = false,
  options,
) => {
  // Use v1 query format - remove unsupported params like join
  const cleanParams = prepareQueryParams(queryParams);
  const queryString = buildV1QueryString(cleanParams);
  const request = axios.post(
    `${config.apiBaseUrl}/${path}${queryString}`,
    data,
    options,
  );

  return (skipRetry ? request : withRetry(() => request))
    .then((res) => {
      console.debug(`POST /${path} result:`, res.data);
      if (successMessageId) notification('success', successMessageId);
      return res.data;
    })
    .catch((err) => {
      console.debug(`POST /${path} error:`, err.message);
      if (errorMessageId) notification('error', errorMessageId);
      throw err;
    });
};

export const basePatchQuery = (
  path,
  data,
  queryParams,
  successMessageId,
  errorMessageId,
  options,
) => {
  // Use v1 query format - remove unsupported params like join
  const cleanParams = prepareQueryParams(queryParams);
  const queryString = buildV1QueryString(cleanParams);

  return withRetry(() =>
    axios.patch(`${config.apiBaseUrl}/${path}${queryString}`, data, options),
  )
    .then((res) => {
      console.debug(`PATCH /${path} result:`, res.data);
      if (successMessageId) notification('success', successMessageId);
      return res.data;
    })
    .catch((err) => {
      console.debug(`PATCH /${path} error:`, err.message);
      if (errorMessageId) notification('error', errorMessageId);
      throw err;
    });
};

export const baseDeleteQuery = (
  path,
  queryParams,
  successMessageId,
  errorMessageId,
) => {
  // Use v1 query format - remove unsupported params like join
  const cleanParams = prepareQueryParams(queryParams);
  const queryString = buildV1QueryString(cleanParams);

  return withRetry(() =>
    axios.delete(`${config.apiBaseUrl}/${path}${queryString}`),
  )
    .then((res) => {
      console.debug(`DELETE /${path} result:`, res.data);
      if (successMessageId) notification('success', successMessageId);
      return res.data;
    })
    .catch((err) => {
      console.debug(`DELETE /${path} error:`, err.message);
      if (errorMessageId) notification('error', errorMessageId);
      throw err;
    });
};

export const getMenu = (menuId, queryParams = {}) => {
  // Note: join is not supported in v1 API - backend auto-includes relations
  return baseGetQuery(
    `menus/${menuId}`,
    { ...queryParams },
    null,
    'getMenuError',
  );
};

export const uploadSingleImage = (file, storeId) => {
  const basePath = storeId ? `manager/stores/${storeId}/images` : 'media/image';
  return basePostQuery(basePath, null, null, null, 'imageUploadError').then(
    (res) => {
      // prepare file for upload
      const formData = new FormData();
      formData.append('file', file);

      return axios
        .post(res.uploadUrl, formData, {
          // remove Authorization because this is not the SB Backend
          transformRequest: (data, headers) => {
            delete headers.common['Authorization'];
            return data;
          },
          timeout: 180 * 1000, // set timeout here to 180 sec
          withCredentials: false, // do not send cookies
        })
        .then(() =>
          basePostQuery(
            `${basePath}/${res.id}/confirm-upload`,
            null,
            null,
            null,
            'imageUploadError',
          ),
        )
        .catch((err) => {
          console.debug('Image Upload error:', err.message);
          notification('error', 'imageUploadError');
        });
    },
  );
};

export const updateMenuEntryDisplayType = (
  id,
  displayType,
  onSuccessCallback,
) => {
  basePatchQuery(
    `menu-entries/${id}`,
    { displayType: displayType },
    null,
    'changesSaved',
    'changesSavedError',
  ).then(() => onSuccessCallback());
};

export const getMe = () => baseGetQuery('user/me');

// Fetch store by slug for public/guest access
export const getStoreBySlug = (slug) =>
  baseGetQuery(`public/stores/by-slug/${slug}`);

// Fetch store by ID for admin access
export const getStoreByIdAdmin = (id) => baseGetQuery(`admin/stores/${id}`);

// Fetch store for manager/store-owner (returns their own store)
export const getStoreManager = () =>
  baseGetQuery('manager/stores').then(({ data }) => data[0]);

// Fetch store by ID for manager access
export const getStoreByIdManager = (id) => baseGetQuery(`manager/stores/${id}`);

// Legacy function - use role-specific functions instead
// For authenticated store owners/managers: use manager endpoint
// For admins: use admin endpoint
// For guests/public: use public endpoint
export const getStore = (slugOrId, isManagerMode = false, isAdmin = false) => {
  if (isAdmin) {
    // Admin fetching by ID
    return baseGetQuery(`admin/stores/${slugOrId}`);
  }
  if (isManagerMode) {
    // Store owner/manager fetching their own store
    return baseGetQuery('manager/stores').then(({ data }) => data[0]);
  }
  // Public/guest access by slug
  return baseGetQuery(`public/stores/by-slug/${slugOrId}`);
};

export const getMenuEntries = (
  menuId,
  isAdminMode = false,
  id = null,
  storeId = null,
) => {
  const path = isAdminMode
    ? `manager/stores/${storeId}/menu/${menuId}/menu-entries` +
      (id ? `/${id}` : '')
    : `public/menus/${menuId}/menu-entries` + (id ? `/${id}` : '');

  // Helper to get ID from relation (could be object with id or just the id string)
  const getRelationId = (relation) => {
    if (!relation) return null;
    return typeof relation === 'string' ? relation : relation.id;
  };

  return baseGetQuery(path).then((res) => {
    const data = Array.isArray(res) ? res : res?.data || [];

    // A category is hidden when its CategoryMenuEntry has displayType 'hidden'.
    // The public endpoint strips hidden CategoryMenuEntries entirely, so in
    // non-admin mode the absence of a CategoryMenuEntry for a referenced
    // category means that category is hidden. In admin mode hidden entries are
    // returned, so their displayType is trusted as-is.
    const enrichCategory = (category) => {
      const categoryId = getRelationId(category);
      if (!categoryId) return category;
      const categoryMenuEntry = data.find(
        (menuEntry) =>
          menuEntry.type === 'CategoryMenuEntry' &&
          getRelationId(menuEntry.categoryEntry) === categoryId,
      );
      const categoryObj =
        typeof category === 'object' ? category : { id: category };
      return {
        ...categoryObj,
        displayType: categoryMenuEntry
          ? categoryMenuEntry.displayType
          : isAdminMode
          ? undefined
          : 'hidden',
      };
    };

    const menuEntries = data.map((menuEntryData) => {
      if (!getRelationId(menuEntryData?.category)) return menuEntryData;
      return {
        ...menuEntryData,
        category: enrichCategory(menuEntryData.category),
      };
    });

    // Keep items that live in hidden categories (they must remain findable);
    // only drop entries that are explicitly hidden themselves (relevant for
    // admin mode, where nothing is stripped server-side). Ordering of
    // hidden-category items is gated separately in the UI.
    return menuEntries.filter(
      (menuEntry) => isAdminMode || menuEntry.displayType !== 'hidden',
    );
  });
};

export const getItemCategories = (
  menuId,
  isAdminMode = false,
  id = null,
  storeId = null,
) => {
  const path = isAdminMode
    ? `manager/stores/${storeId}/item-categories` + (id ? `/${id}` : '')
    : `public/menus/${menuId}/item-categories` + (id ? `/${id}` : '');

  // Note: join is not supported in v1 API - backend auto-includes relations
  return baseGetQuery(path).then((res) =>
    Array.isArray(res) ? res : res?.data || [],
  );
};

export const getFoodAllergens = (id = null) => {
  const path = 'public/food-allergens' + (id ? `/${id}` : '');

  return baseGetQuery(path).then((res) =>
    Array.isArray(res) ? res : res?.data || [],
  );
};

export const getFoodAdditives = (id = null) => {
  const path = 'public/food-additives' + (id ? `/${id}` : '');

  return baseGetQuery(path).then((res) =>
    Array.isArray(res) ? res : res?.data || [],
  );
};

export const getFoodPreferences = (id = null) => {
  const path = 'public/food-preferences' + (id ? `/${id}` : '');

  return baseGetQuery(path).then((res) =>
    Array.isArray(res) ? res : res?.data || [],
  );
};

export const getFoodIngredients = (id = null) => {
  const path = 'public/food-ingredients' + (id ? `/${id}` : '');

  return baseGetQuery(path).then((res) =>
    Array.isArray(res) ? res : res?.data || [],
  );
};

export const getFoodItemAdditions = (
  isAdminMode,
  menuId = null,
  id,
  storeId = null,
) => {
  const path = isAdminMode
    ? `manager/stores/${storeId}/item-additions` + (id ? `/${id}` : '')
    : `public/menus/${menuId}/item-additions` + (id ? `/${id}` : '');

  return baseGetQuery(path).then((res) =>
    Array.isArray(res) ? res : res?.data || [],
  );
};

export const getAdditionGroups = (
  isAdminMode,
  menuId = null,
  id,
  storeId = null,
) => {
  const path = isAdminMode
    ? `manager/stores/${storeId}/item-addition-groups` + (id ? `/${id}` : '')
    : `public/menus/${menuId}/item-addition-groups` + (id ? `/${id}` : '');

  return baseGetQuery(path).then((res) =>
    Array.isArray(res) ? res : res?.data || [],
  );
};

export const createGuest = (payload, setWebSocketToken, force = false) => {
  const guestToken = localStorage.getItem('guestToken');
  if (!guestToken || force) {
    return basePostQuery('user/guests', payload).then((guest) => {
      TagManager.dataLayer({
        dataLayer: {
          event: 'CreateGuestAccount',
        },
      });
      setRequestDefaults(guest?.token, true, setWebSocketToken);

      return guest;
    });
  } else {
    console.warn('guestToken already exists');
    return Promise.resolve(null);
  }
};

export const adoptGuest = () => {
  const guestToken = localStorage.getItem('guestToken');
  const adoptGuestMode = localStorage.getItem('adoptGuestMode');

  if (adoptGuestMode === 'yes' && guestToken) {
    return axios
      .get(`${config.apiBaseUrl}/user/me`, {
        // set Authorization because we need to access as guest
        transformRequest: (data, headers) => {
          headers.common['Authorization'] = guestToken;
          return data;
        },
      })
      .then(({ data }) => {
        return basePostQuery(`user/guests/adopt`, {
          token: guestToken,
        });
      })
      .then(() => {
        localStorage.removeItem('guestToken');
        localStorage.removeItem('adoptGuestMode');
        console.log('Successfully adopted guest!');
      })
      .catch((err) => {
        // giving up. Backup guestToken and delete adoptGuestMode
        localStorage.setItem('guestTokenBackup', guestToken);
        localStorage.removeItem('guestToken');
        localStorage.removeItem('adoptGuestMode');
        console.error('Could not adopt guest: ' + err?.message);
      });
  } else {
    console.log('No guestToken or adoptGuestMode found');
    return Promise.resolve();
  }
};

export const getOrders = (
  id = null,
  queryParams = {},
  isAdminMode = false,
  storeId = null,
) => {
  let path;
  const useV1 = isAdminMode && storeId;
  if (isAdminMode) {
    // Manager endpoint requires storeId
    path = storeId
      ? `manager/stores/${storeId}/orders${id ? `/${id}` : ''}`
      : 'user/orders' + (id ? `/${id}` : ''); // fallback to user orders if no storeId
  } else {
    path = 'user/orders' + (id ? `/${id}` : '');
  }
  // Use v1 query format for manager orders (no limit, no join - backend auto-includes relations)
  return useV1
    ? baseGetQueryV1(path, queryParams).then(({ data }) => data)
    : baseGetQuery(path, queryParams).then(({ data }) => data);
};

export const getOrderStats = (queryParams = {}, storeId) => {
  const path = `manager/stores/${storeId}/orders/stats`;
  // Stats endpoint uses v1 format (no limit, no join)
  return baseGetQueryV1(path, queryParams);
};

export const getStoreTables = (queryParams = {}, storeId) => {
  const path = `manager/stores/${storeId}/tables`;
  return baseGetQuery(path, { ...queryParams }).then(({ data }) => data);
};

export const getStoreTablesPublic = (storeId) => {
  const path = `public/stores/${storeId}/tables`;
  return baseGetQuery(path).then(({ data }) => data);
};

/**
 * Resolve one of a store's tables by the name the guest typed.
 *
 * The public list deliberately omits `slug` (it is a table's access token), so
 * the name -> slug step has to happen server-side. Returns the full table.
 */
export const getStoreTableByNamePublic = (storeId, name) => {
  const path = `public/stores/${storeId}/tables/by-name/${encodeURIComponent(
    name,
  )}`;
  return baseGetQuery(path);
};

export const getStoreDiscountCodes = (queryParams = {}, storeId) => {
  const path = `manager/stores/${storeId}/discount-codes`;
  return baseGetQuery(path, { ...queryParams }).then(({ data }) => data);
};

export const getSystemDiscountCodes = (queryParams = {}) => {
  const path = 'admin/system-discount-codes';
  return baseGetQuery(path, { ...queryParams }).then(({ data }) => data);
};

export const getStorePrinterConfigs = (queryParams = {}, storeId) => {
  const path = `manager/stores/${storeId}/printer-configs`;
  return baseGetQuery(path, {
    limit: 25,
    ...queryParams,
  }).then(({ data }) => data);
};

export const getStorePickupStations = (queryParams = {}, storeId) => {
  const path = `manager/stores/${storeId}/pickup-stations`;
  return baseGetQuery(path, {
    limit: 25,
    ...queryParams,
  }).then(({ data }) => data);
};

export const getKiosks = (queryParams = {}, storeId) => {
  const path = `manager/stores/${storeId}/kiosks`;
  // Note: join is not supported in v1 API - backend auto-includes relations
  return baseGetQuery(path, {
    limit: 25,
    ...queryParams,
  }).then(({ data }) => data);
};
export const getBridges = (queryParams = {}, storeId) => {
  const path = `manager/stores/${storeId}/bridges`;
  return baseGetQuery(path, {
    limit: 25,
    ...queryParams,
  }).then(({ data }) => data);
};

export const getAllBridges = (queryParams = {}) => {
  const path = 'admin/bridges';
  return baseGetQuery(path, {
    limit: 25,
    ...queryParams,
  }).then(({ data }) => data);
};

export const getPaymentTerminals = (queryParams = {}, storeId) => {
  const path = `manager/stores/${storeId}/payment-terminals`;
  return baseGetQuery(path, {
    limit: 25,
    ...queryParams,
  }).then(({ data }) => data);
};

export const getPaymentMethods = () => {
  return baseGetQuery('user/me/payment-methods');
};

export const setPaymentMethodDefault = (id) => {
  return basePostQuery('user/me/payment-methods/default', {
    paymentMethodId: id,
  });
};

export const deletePaymentMethod = (id) => {
  return baseDeleteQuery('user/me/payment-methods/' + id);
};

export const getSpecificOrder = (id) => {
  const path = 'orders' + (id ? `/${id}` : '') + '/store';
  return baseGetQuery(path);
};

export const getOrder = (id) => {
  const path = 'user/orders/' + id;
  return baseGetQuery(path);
};

export const getStoreOrder = (id, storeId) => {
  const path = `manager/stores/${storeId}/orders/${id}`;
  return baseGetQuery(path);
};

export const getCustomerOrder = (id) => {
  const path = 'user/orders/' + id;
  return baseGetQuery(path);
};

// Claims an unfinished (PENDING) order for the current session via the
// single-use resume token from the WhatsApp "finish your order" link. This is
// what allows the checkout to be resumed from a different browser session
// (e.g. WhatsApp's in-app browser), where the order is not owned by the local
// guest yet.
export const resumeCustomerOrder = (id, resumeToken) => {
  const path = 'user/orders/' + id + '/resume';
  return basePostQuery(
    path,
    { resumeToken },
    null,
    null,
    null,
    false,
    { headers: { 'X-API-Version': '2' } },
  );
};

export const getBillingAddresses = () => {
  return baseGetQuery('user/addresses/billing').then(({ data }) => data);
};

export const createBillingAddress = () => {
  return basePostQuery('user/addresses/billing');
};

export const updateBillingAddress = (id, payload) => {
  return basePatchQuery('user/addresses/billing/' + id, payload);
};

export const deleteBillingAddress = (id) => {
  return baseDeleteQuery('user/addresses/billing/' + id);
};

export const getStripeBalance = (storeId, id) => {
  return baseGetQuery(
    'manager/stores/' +
      storeId +
      '/payment-processors/stripe/' +
      id +
      '/balance',
  );
};

export const getDeliveryAddresses = () => {
  return baseGetQuery('user/addresses/delivery').then(({ data }) => data);
};

export const createDeliveryAddress = (payload) => {
  return basePostQuery('user/addresses/delivery', payload);
};

export const updateDeliveryAddress = (id, payload) => {
  return basePatchQuery('user/addresses/delivery/' + id, payload);
};

export const deleteDeliveryAddress = (id) => {
  return baseDeleteQuery('user/addresses/delivery/' + id);
};

// Service times are now part of the Store entity - fetch via manager/stores/:id
export const getServiceTimes = (storeId) => {
  if (!storeId) {
    console.error('Store ID is not defined');
    return Promise.resolve(null);
  }

  return baseGetQuery(`manager/stores/${storeId}`)
    .then((store) => {
      return store?.serviceTimes || [];
    })
    .catch((err) => {
      if (err.response && err.response.status === 404) {
        console.warn('Service times not found for store:', storeId);
        return [];
      }
      throw err;
    });
};

// NOTE: createServiceTimes endpoint no longer exists - use PATCH manager/stores/:id with serviceTimes
export const createServiceTimes = (storeId, payload) => {
  return basePatchQuery(`manager/stores/${storeId}`, {
    serviceTimes: payload,
  }).then((store) => store?.serviceTimes || []);
};

export const updateMe = (payload) => {
  return basePatchQuery('user/me', payload);
};

// Calculate current service time from store's serviceTimes
export const getCurrentServiceTime = (storeId) => {
  if (!storeId) {
    console.error('Store ID is not defined');
    return Promise.resolve(null);
  }

  return baseGetQuery(`manager/stores/${storeId}`)
    .then((store) => {
      const serviceTimes = store?.serviceTimes || [];
      const now = new Date();

      // Find service time that contains current time
      const currentServiceTime = serviceTimes.find((st) => {
        const startAt = new Date(st.startAt);
        const endAt = new Date(st.endAt);
        return now >= startAt && now <= endAt;
      });

      return currentServiceTime || null;
    })
    .catch((err) => {
      if (err.response && err.response.status === 404) {
        console.debug(`Current service time not found for store ${storeId}.`);
        return null;
      }
      throw err;
    });
};

// Calculate next start service time from store's serviceTimes
export const getNextStartServiceTime = (storeId) => {
  if (!storeId) {
    console.error('Store ID is not defined');
    return Promise.resolve(null);
  }

  return baseGetQuery(`manager/stores/${storeId}`)
    .then((store) => {
      const serviceTimes = store?.serviceTimes || [];
      const now = new Date();

      // Find the next service time that starts after now
      const futureServiceTimes = serviceTimes
        .filter((st) => new Date(st.startAt) > now)
        .sort((a, b) => new Date(a.startAt) - new Date(b.startAt));

      return futureServiceTimes[0] || null;
    })
    .catch((err) => {
      if (err.response && err.response.status === 404) {
        console.debug(
          `Next start service time not found for store ${storeId}.`,
        );
        return null;
      }
      throw err;
    });
};

export const updateTableCart = (storeId, tableId) => {
  return basePostQuery(
    `manager/stores/${storeId}/pos-integrations/${tableId}/update-table-cart`,
    null,
    null,
    null,
    null,
    true,
  );
};

export const getCustomerProfile = (id) => {
  return baseGetQuery('user/customer-profiles/' + id);
};

export const updateCustomerProfile = (id, payload) => {
  return basePatchQuery('user/customer-profiles/' + id, payload);
};

export const getHelloTessSetup = (storeId, payload) => {
  return basePostQuery(
    `manager/stores/${storeId}/pos-integrations/hellotess/setup`,
    payload,
  );
};

export const getPosIntegrations = (storeId) => {
  return baseGetQuery(`manager/stores/${storeId}/pos-integrations`);
};

export const getPosIntegrationItemDataMappings = (storeId, id) => {
  return baseGetQuery(
    `manager/stores/${storeId}/pos-integrations/${id}/item-data`,
  );
};

export const getPosIntegrationItemAdditionMappings = (storeId, id) => {
  return baseGetQuery(
    `manager/stores/${storeId}/pos-integrations/${id}/item-additions`,
  );
};

export const getPosIntegrationItemAdditionGroupMappings = (storeId, id) => {
  return baseGetQuery(
    `manager/stores/${storeId}/pos-integrations/${id}/item-addition-groups`,
  );
};

export const syncPosIntegration = (storeId, id) => {
  return basePostQuery(
    `manager/stores/${storeId}/pos-integrations/${id}/sync`,
    null,
    null,
    null,
    null,
    true,
  );
};

export const getPosIntegrationTableMappings = (storeId, id) => {
  if (id === undefined) {
    console.error('ID is undefined');
    return Promise.resolve(null);
  }

  return baseGetQuery(
    `manager/stores/${storeId}/pos-integrations/${id}/tables`,
  ) /* .catch((err) => {
    if (err.response && err.response.status === 400) {
      console.error('Bad request error:', err);
      return null;
    } else {
      throw err;
    }
  }) */;
};

export const getStores = (queryParams = {}, showDeleted = false) => {
  const path = 'admin/stores';

  // Build filters array - start with deletedAt filter if showing deleted
  const filters = [];
  if (showDeleted) {
    filters.push('deletedAt||$notnull');
  }

  // Add any filters from queryParams
  if (queryParams.filter) {
    const paramFilters = Array.isArray(queryParams.filter)
      ? queryParams.filter
      : [queryParams.filter];
    // Only add valid filters (not undefined/null)
    paramFilters.forEach((f) => {
      if (f && typeof f === 'string') {
        filters.push(f);
      }
    });
  }

  // Build final query params for v1 API
  const finalParams = {
    ...queryParams,
    filter: filters.length > 0 ? filters : undefined,
  };

  return baseGetQuery(path, finalParams).then((res) => res);
};

// On-behalf-of functionality now uses URL query param with storeId instead of backend endpoint
export const setOnBehalfOfStoreOwner = (selectedStore) => {
  // Navigate to /store/{slug}?storeId={storeId} - using storeId in query param for admin/manager access
  const newTabUrl = `${window.location.origin}/store/${selectedStore?.slug}?storeId=${selectedStore?.id}`;
  window.open(newTabUrl, '_blank');
  return Promise.resolve();
};

export const deleteOnBehalfOfStoreOwner = (
  selectedStore,
  redirect,
  fetchMe,
) => {
  // Redirect to admin area
  if (fetchMe) fetchMe();
  if (redirect) redirect();
  return Promise.resolve();
};

// Helper to get storeId from URL query param for admin/manager "on behalf of" mode
// URL format: /store/{slug}?storeId={storeId}
export const getOnBehalfOfStoreId = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('storeId');
};

export const getAllStoresStats = (queryParams = {}) => {
  const path = 'admin/orders/stats';
  // Admin stats endpoint uses v1 format
  return baseGetQueryV1(path, queryParams);
};

export const getStoreOrders = (queryParams, storeId = null) => {
  const path = storeId
    ? `manager/stores/${storeId}/orders`
    : 'manager/stores/orders'; // fallback - may need adjustment
  // Use v1 query format (no limit, no join - backend auto-includes relations)
  return baseGetQueryV1(path, queryParams).then((res) => res);
};

export const getInvoices = (queryParams, storeId) => {
  const path = `manager/stores/${storeId}/invoices`;
  return baseGetQuery(path, queryParams).then((res) => res);
};

export const getStoreOrdersCsv = (date, merchantProfileId, storeId) => {
  const path = `manager/stores/${storeId}/merchant-profiles/${merchantProfileId}/orders/${date}`;
  return baseGetQuery(path);
};

export const getMonthlyInvoice = (date, id, configBlob = {}) => {
  const path = `merchant-profiles/${id}/invoices/${date}`;
  const getBlobQuery = (
    path,
    queryParams,
    successMessageId,
    errorMessageId,
    configBlob,
  ) => {
    // Use v1 query format - remove unsupported params like join
    const cleanParams = prepareQueryParams(queryParams);
    const queryString = buildV1QueryString(cleanParams);

    return withRetry(() =>
      axios.get(`${config.apiBaseUrl}/${path}${queryString}`, configBlob),
    )
      .then((res) => {
        console.debug(`GET /${path} result:`, res.data);
        if (successMessageId) notification('success', successMessageId);
        return res.data;
      })
      .catch((err) => {
        console.debug(`GET /${path} error:`, err.message);
        if (errorMessageId) notification('error', errorMessageId);
        throw err;
      });
  };

  return getBlobQuery(path, id, null, null, configBlob);
};

export const handleChangePassword = () => {
  basePostQuery('user/me/reset-password', {
    redirectUrl: window.location.href,
  })
    .then((res) => (window.location.href = res.resetLink))
    .catch((error) => {
      notification('warning', 'passwordChangeError');
    });
};

export const createTableReservation = (payload) => {
  return basePostQuery('table-reservations', payload, null, null, null, true);
};

export const getConnectors = (storeId) => {
  return baseGetQuery(`manager/stores/${storeId}/connectors`);
};

export const getLogs = (storeId) => {
  return baseGetQuery(`manager/stores/${storeId}/advanced-logs`);
};

export const getAreas = (storeId) => {
  const path = `manager/stores/${storeId}/areas`;
  return baseGetQuery(path).then(({ data }) => data);
};

export const getMenuItems = (
  isAdminMode,
  menuId = null,
  id,
  storeId = null,
) => {
  const path = isAdminMode
    ? `manager/stores/${storeId}/items` + (id ? `/${id}` : '')
    : `public/menus/${menuId}/items` + (id ? `/${id}` : '');

  return baseGetQuery(path).then((res) =>
    Array.isArray(res) ? res : res?.data || [],
  );
};

export const getItemDatas = (
  isAdminMode,
  menuId = null,
  id,
  storeId = null,
) => {
  const path = isAdminMode
    ? `manager/stores/${storeId}/item-data` + (id ? `/${id}` : '')
    : `public/menus/${menuId}/item-data` + (id ? `/${id}` : '');

  return baseGetQuery(path).then((res) =>
    Array.isArray(res) ? res : res?.data || [],
  );
};

export const getDailyLimits = (menuId) => {
  const path = `public/menus/${menuId}/item-data/daily-limits`;
  return baseGetQuery(path).then((res) => (Array.isArray(res) ? res : []));
};

// Order Config API functions
export const getOrderConfigPublic = (storeId, orderConfigId) => {
  const path = `public/stores/${storeId}/order-configs/${orderConfigId}`;
  return baseGetQuery(path);
};

export const getOrderConfigManager = (storeId, orderConfigId) => {
  const path = `manager/stores/${storeId}/order-configs/${orderConfigId}`;
  return baseGetQuery(path);
};

export const updateOrderConfig = (storeId, orderConfigId, payload) => {
  const path = `manager/stores/${storeId}/order-configs/${orderConfigId}`;
  return basePatchQuery(
    path,
    payload,
    null,
    'changesSaved',
    'changesSavedError',
  );
};

export const getNotificationMethodConfigs = (storeId) => {
  return baseGetQuery(`manager/stores/${storeId}/notification-methods`);
};

// ============================================================================
// V2 Order API
// ============================================================================

const v2Headers = { headers: { 'X-API-Version': '2' } };

export const createOrderV2 = (type, data) => {
  return basePostQuery(`user/orders/${type}`, data, null, null, null, false, v2Headers);
};

export const updateOrderV2 = (type, orderId, data) => {
  return basePatchQuery(`user/orders/${type}/${orderId}`, data, null, null, null, v2Headers);
};

export const createOrderPaymentV2 = (orderId, data = {}) => {
  return basePostQuery(`user/orders/${orderId}/payment`, data, null, null, null, false, v2Headers);
};

export const submitOrderV2 = (type, orderId) => {
  return updateOrderV2(type, orderId, { status: 'submitted' });
};
